import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GraphQLClient } from "../../../../../shared/gql-client";
import { z } from "zod";
import { CrudTableSliceModel } from "./crudSliceModel";
import CrudService from "../../../crudGraphql/infrastructure/services/crudGraphqlService";
import {
  CustomFilters,
  GraphqlFilterRoot,
} from "../../../crudGraphql/domain/models/graphqlModel";
import { SortingState } from "@tanstack/react-table";

const crudAPI = new CrudService();

export const crudReadAll = createAsyncThunk(
  "crudReadAll",
  async (
    {
      entity,
      pageIndex,
      pageSize,
      filters,
      sorting,
    }: {
      entity: string;
      pageIndex: number;
      pageSize: number;
      filters?: GraphqlFilterRoot | CustomFilters;
      sorting?: SortingState;
      queryExclude?: string[];
    },
    thunkAPI,
  ) => {
    const { client } = thunkAPI.extra as { client: GraphQLClient };
    const { crudTable } = thunkAPI.getState() as {
      crudTable: CrudTableSliceModel;
    };
    crudAPI.init(client, crudTable.schema);
    try {
      return await crudAPI.readAll(
        entity,
        crudTable.prefix,
        pageIndex + 1,
        pageSize,
        filters,
        sorting,
        crudTable.queryExclude,
      );
    } catch (err) {
      console.error(err);
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const crudExport = createAsyncThunk(
  "crudExport",
  async (
    {
      queryFunction,
      filters,
      queryData,
    }: {
      queryFunction: string;
      queryData: (string | { [key: string]: string | unknown[] })[];
      filters?: GraphqlFilterRoot;
    },
    thunkAPI,
  ) => {
    const { client } = thunkAPI.extra as { client: GraphQLClient };
    const { crudTable } = thunkAPI.getState() as {
      crudTable: CrudTableSliceModel;
    };
    crudAPI.init(client, crudTable.schema);
    try {
      return crudAPI.export(queryFunction, queryData, filters);
    } catch (err) {
      console.error(err);
      return thunkAPI.rejectWithValue(err);
    }
  },
);

const initialState: CrudTableSliceModel = {
  loading: true,
  data: undefined,
  schema: z.object({}),
  prefix: "",
  queryExclude: [],
  latestRequestId: null,
};

const crudTableSlice = createSlice({
  name: "crudTable",
  initialState,
  reducers: {
    clear: () => {
      return initialState;
    },
    initTable: (
      state,
      action: {
        payload: {
          schema: z.ZodObject<z.ZodRawShape>;
          prefix: string;
          queryExclude?: string[];
        };
      },
    ) => {
      state.schema = action.payload.schema;
      state.prefix = action.payload.prefix;
      state.queryExclude = action.payload.queryExclude || [];
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    removeItem: (state, action) => {
      if (state.data !== undefined) {
        state.data = state.data.filter((item) => item.id !== action.payload);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(crudReadAll.pending, (state, action) => {
      state.loading = true;
      state.latestRequestId = action.meta.requestId;
    });
    builder.addCase(crudReadAll.fulfilled, (state, action) => {
      // Ignore stale responses when a newer request has been issued
      if (action.meta.requestId !== state.latestRequestId) {
        return;
      }
      state.loading = false;
      state.data = (action.payload as { data: object[] }).data;
      state.total = (
        action.payload as { paginatorInfo: { total: number } }
      ).paginatorInfo.total;
    });
    builder.addCase(crudReadAll.rejected, (state, action) => {
      // Only clear loading state if this is the latest request
      if (action.meta.requestId === state.latestRequestId) {
        state.loading = false;
      }
    });
  },
});

export const { clear, setLoading, initTable, removeItem } =
  crudTableSlice.actions;

export default crudTableSlice.reducer;
